import { useEffect, useState } from "react";
import type { LicenseCheckResult } from "../config";

// Offline license check (Electron only, for now — see .md/licensing-proposal.md).
// No network call, no data collected: the main process verifies a locally
// stored key's signature/expiry and reports the result here.
const UNLICENSED: LicenseCheckResult = { valid: false, reason: "none" };

let currentStatus: LicenseCheckResult = UNLICENSED;
const listeners = new Set<(s: LicenseCheckResult) => void>();

const setStatus = (s: LicenseCheckResult) => {
  currentStatus = s;
  listeners.forEach((l) => l(s));
};

const refresh = () => {
  if (!window.electronAPI?.getLicenseStatus) return;
  window.electronAPI
    .getLicenseStatus()
    .then(setStatus)
    .catch(() => undefined);
};

if (typeof window !== "undefined" && window.electronAPI) {
  refresh();
  // A long-running session could cross the key's expiry moment while the
  // app stays open; re-check periodically so the lock reflects that without
  // requiring a relaunch. Cheap — it's a local date comparison, no network.
  setInterval(refresh, 15 * 60 * 1000);
}

/** Validate and store a license key entered by the user. */
export const activateLicense = async (key: string): Promise<LicenseCheckResult> => {
  if (!window.electronAPI?.activateLicense) return UNLICENSED;
  const result = await window.electronAPI.activateLicense(key);
  setStatus(result);
  return result;
};

/** Clear the stored license key (e.g. to switch to a different one). */
export const deactivateLicense = async (): Promise<void> => {
  if (window.electronAPI?.deactivateLicense) {
    await window.electronAPI.deactivateLicense();
  }
  setStatus(UNLICENSED);
};

export const getLicenseStatus = (): LicenseCheckResult => currentStatus;

export const subscribeLicense = (cb: (s: LicenseCheckResult) => void): (() => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

/** React hook for the current license status. */
export const useLicenseStatus = (): LicenseCheckResult => {
  const [status, setStatusHook] = useState<LicenseCheckResult>(currentStatus);
  useEffect(() => subscribeLicense(setStatusHook), []);
  return status;
};
